/**
 * Ephemeral mid-turn status flash card (feat-80).
 *
 * Shown while /converse is in flight; parent sets visible=false when the
 * response arrives so only the assistant message remains.
 * Viewport shows STATUS_VISIBLE_LINES full rows; overflow scrolls, and each
 * event append resets scroll to the bottom.
 */

import React, { useEffect, useRef } from 'react';
import { BtnIcon } from '@growthaxl/web-foundation';
import type { ConverseStatusEvent } from '../../hooks/useConverseStatusStream';

export interface ConverseStatusCardProps {
  events: ConverseStatusEvent[];
  visible: boolean;
  className?: string;
}

/** How many status rows stay fully visible without mid-line clipping. */
export const STATUS_VISIBLE_LINES = 5;

/** Caption × leading-snug — keep in sync with text-caption / leading-snug tokens. */
const STATUS_ROW_HEIGHT_CSS =
  'calc(var(--gaxl-size-caption) * var(--gaxl-leading-snug))';
const STATUS_VIEWPORT_MAX_HEIGHT_CSS = `calc(var(--gaxl-size-caption) * var(--gaxl-leading-snug) * ${STATUS_VISIBLE_LINES})`;

export function ConverseStatusCard({
  events,
  visible,
  className,
}: ConverseStatusCardProps): React.ReactElement | null {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [events]);

  if (!visible || events.length === 0) return null;

  return (
    <div
      className={`mx-3 mb-2 rounded-8 overflow-hidden border border-border-default/50 bg-surface/70 backdrop-blur-md shadow-lg ${className ?? ''}`}
      data-testid="converse-status-card"
      role="status"
      aria-live="polite"
      aria-label="Status updates"
    >
      <div className="px-3 py-2">
        <div
          ref={scrollRef}
          className="overflow-y-auto"
          style={{ maxHeight: STATUS_VIEWPORT_MAX_HEIGHT_CSS }}
          data-testid="converse-status-scroll"
        >
          <ul className="m-0 list-none p-0" role="list">
            {events.map((ev, i) => {
              const isLatest = i === events.length - 1;
              return (
                <li
                  key={`${ev.seq}-${i}`}
                  className={`flex items-center ${
                    isLatest
                      ? 'text-caption leading-snug text-text-primary'
                      : 'text-caption leading-snug text-text-secondary opacity-70'
                  }`}
                  style={{ height: STATUS_ROW_HEIGHT_CSS }}
                >
                  {isLatest ? (
                    <span className="inline-flex items-center gap-1.5 motion-safe:animate-pulse">
                      <span
                        className="inline-block size-1.5 shrink-0 rounded-full bg-esa-accent"
                        aria-hidden="true"
                      />
                      <span>{ev.message}</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      <BtnIcon name="check" size={10} className="shrink-0 text-success" />
                      <span>{ev.message}</span>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
